/**
 * Cockpit visual editor — the SPEC-EDITOR shell.
 *
 * The real visual authoring experience for a CockpitSpec cockpit (the readiness
 * audit's #1 blocker: before this, a user could only build a CockpitSpec via the
 * SQL seeder). Five panes, all driven by the pure `cockpit-editor-model`:
 *
 *   +------------------------------------------------------------------+
 *   |  Header (from ArgusLiveEditor) — version dropdown / Save / Publish |
 *   +-------------------+----------------------------+-----------------+
 *   |  Palette          |  Live preview              |  Inspector      |
 *   |  (insert a node)  |  (CockpitRenderer)         |  Tree | Props   |
 *   |                   |                            |  Pages | Bind   |
 *   +-------------------+----------------------------+-----------------+
 *
 * Authoring loop: click a palette tile → a node is inserted under the selected
 * parent → the tree + preview update → select the node → edit its props in the
 * schema-driven form → `parseCockpitSpec` re-validates on every edit and surfaces
 * structured errors inline. Multi-page via the page tabs. AI author seeds the
 * whole spec from a prompt.
 *
 * Save persists the spec as `sandboxConfig: { cockpitSpec }` on a new version
 * (parseCockpitSpec MUST pass first); Publish does the same through the publish
 * flow. RBAC mirrors the raw-HTML editor (edit grant + write permission).
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Rocket, Save, Loader2, ListTree, SlidersHorizontal, Layers, Database,
  AlertCircle, CheckCircle2, Sparkles, RefreshCw,
} from 'lucide-react';
import {
  parseCockpitSpec,
  getCockpitComponent,
  type CockpitSpec,
} from '@argusai/shared';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTheme } from '@/components/theme-provider';
import { toast } from 'sonner';

import {
  type ArgusLiveDashboard,
  type ArgusLiveVersion,
  useCreateArgusLiveVersion,
  usePublishArgusLiveDashboard,
  type AuthorCockpitResult,
} from '@/lib/use-argus-live';
import type { DashboardAffordances } from '@/lib/argus-live-helpers';

import { CockpitRenderer } from './cockpit-renderer';
import { CockpitPalette } from './cockpit-palette';
import { CockpitTree } from './cockpit-tree';
import { CockpitPropsForm } from './cockpit-props-form';
import { CockpitPageTabs, DeclaredRefsPanel } from './cockpit-page-manager';
import { CockpitAiAuthorPanel } from './cockpit-ai-author-panel';
import {
  MAIN_PAGE_INDEX,
  editorPages,
  pageRootAt,
  findNode,
  deriveFields,
  insertNode,
  updateNodeProps,
  deleteNode,
  moveNode,
  addPage,
  renamePage,
  reorderPage,
  deletePage,
  canAddPage as canAddPageFn,
  addBindingId,
  removeBindingId,
  addActionId,
  removeActionId,
} from './cockpit-editor-model';

export interface CockpitSpecEditorProps {
  dashboard: ArgusLiveDashboard;
  /** The version whose spec seeds the editor (null = brand-new). */
  seedVersion: ArgusLiveVersion | null;
  /** The starting spec (already extracted + validated by the caller). */
  initialSpec: CockpitSpec;
  affordances: DashboardAffordances;
  /** Suggested rev label for the next save. */
  initialRevLabel: string;
}

type InspectorTab = 'tree' | 'props' | 'pages' | 'bindings';

export function CockpitSpecEditor({
  dashboard,
  initialSpec,
  affordances,
  initialRevLabel,
}: CockpitSpecEditorProps) {
  const { mode } = useTheme();
  const readOnly = affordances.isReadOnly;

  // ── Working state ──────────────────────────────────────────────────────
  const [spec, setSpec] = useState<CockpitSpec>(initialSpec);
  const [activePageIndex, setActivePageIndex] = useState<number>(MAIN_PAGE_INDEX);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(initialSpec.root.id);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('tree');
  const [revLabel, setRevLabel] = useState(initialRevLabel);
  const [isSaving, setIsSaving] = useState(false);
  const [savedSpecJson, setSavedSpecJson] = useState<string>(() => JSON.stringify(initialSpec));

  const createVersion = useCreateArgusLiveVersion();
  const publishMutation = usePublishArgusLiveDashboard();

  // ── Derived: pages, active page root, selected node, validation ────────
  const pages = useMemo(() => editorPages(spec), [spec]);
  const activeRoot = useMemo(() => pageRootAt(spec, activePageIndex), [spec, activePageIndex]);
  const selected = useMemo(
    () => (selectedNodeId ? findNode(activeRoot, selectedNodeId) : null),
    [activeRoot, selectedNodeId],
  );
  const selectedNode = selected?.node ?? null;
  const selectedFields = useMemo(
    () => (selectedNode ? deriveFields(selectedNode.component) : []),
    [selectedNode],
  );

  const validation = useMemo(() => parseCockpitSpec(spec), [spec]);
  const isDirty = useMemo(() => JSON.stringify(spec) !== savedSpecJson, [spec, savedSpecJson]);

  // Per-node prop errors for the props form, keyed by the path under `props.`.
  const selectedErrorsByPath = useMemo(() => {
    if (validation.ok || !selectedNode) return {};
    const map: Record<string, string> = {};
    for (const e of validation.errors) {
      // Paths look like `root.children.2.props.title` or
      // `pages.0.root.children.1.props.label`. We only need the segment AFTER
      // `props.` for the field path, scoped to the selected node — but the
      // simplest robust heuristic is to match any error whose message names the
      // selected component AND carries a `props.` segment.
      const idx = e.path.indexOf('props.');
      if (idx === -1) continue;
      const propPath = e.path.slice(idx + 'props.'.length);
      // Scope to the selected node: the error message is prefixed `[Component]`.
      const comp = getCockpitComponent(selectedNode.component);
      if (comp && e.message.startsWith(`[${comp.slug}]`)) {
        map[propPath] = e.message.replace(/^\[[^\]]+\]\s*/, '');
      }
    }
    return map;
  }, [validation, selectedNode]);

  // ── Mutators (all immutable; re-validate happens via the memo above) ───
  const mutate = useCallback((next: CockpitSpec) => setSpec(next), []);

  const handleInsert = useCallback(
    (slug: string) => {
      if (readOnly) return;
      const parentId = selectedNode ? selectedNode.id : activeRoot.id;
      const { spec: next, nodeId } = insertNode({
        spec,
        pageIndex: activePageIndex,
        parentId,
        slug,
      });
      mutate(next);
      setSelectedNodeId(nodeId);
      setInspectorTab('props');
    },
    [readOnly, selectedNode, activeRoot, spec, activePageIndex, mutate],
  );

  const handlePropsChange = useCallback(
    (props: Record<string, unknown>) => {
      if (readOnly || !selectedNodeId) return;
      mutate(updateNodeProps(spec, activePageIndex, selectedNodeId, props));
    },
    [readOnly, selectedNodeId, spec, activePageIndex, mutate],
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (readOnly) return;
      mutate(deleteNode(spec, activePageIndex, id));
      if (id === selectedNodeId) setSelectedNodeId(activeRoot.id);
    },
    [readOnly, spec, activePageIndex, selectedNodeId, activeRoot, mutate],
  );

  const handleMove = useCallback(
    (id: string, direction: 'up' | 'down') => {
      if (readOnly) return;
      mutate(moveNode(spec, activePageIndex, id, direction));
    },
    [readOnly, spec, activePageIndex, mutate],
  );

  const handleSelectPage = useCallback(
    (index: number) => {
      setActivePageIndex(index);
      const root = pageRootAt(spec, index);
      setSelectedNodeId(root.id);
    },
    [spec],
  );

  const handleAddPage = useCallback(() => {
    if (readOnly) return;
    const { spec: next, pageId } = addPage(spec);
    if (!pageId) return;
    mutate(next);
    const newIndex = (next.pages?.length ?? 1) - 1;
    setActivePageIndex(newIndex);
    setSelectedNodeId(next.pages![newIndex]!.root.id);
    toast.success('Page added');
  }, [readOnly, spec, mutate]);

  const handleRenamePage = useCallback(
    (index: number, title: string) => {
      if (readOnly) return;
      mutate(renamePage(spec, index, title));
    },
    [readOnly, spec, mutate],
  );

  const handleReorderPage = useCallback(
    (index: number, direction: 'left' | 'right') => {
      if (readOnly) return;
      mutate(reorderPage(spec, index, direction));
      // Track the moved page so the active tab stays on it.
      setActivePageIndex(direction === 'left' ? index - 1 : index + 1);
    },
    [readOnly, spec, mutate],
  );

  const handleDeletePage = useCallback(
    (index: number) => {
      if (readOnly) return;
      mutate(deletePage(spec, index));
      setActivePageIndex(MAIN_PAGE_INDEX);
      setSelectedNodeId(spec.root.id);
      toast.success('Page deleted');
    },
    [readOnly, spec, mutate],
  );

  // ── AI author: load the authored spec for review/edit ──────────────────
  const loadSpec = useCallback((candidate: unknown, opts?: { label?: string }) => {
    const parsed = parseCockpitSpec(candidate);
    if (parsed.ok && parsed.spec) {
      setSpec(parsed.spec);
      setActivePageIndex(MAIN_PAGE_INDEX);
      setSelectedNodeId(parsed.spec.root.id);
      setInspectorTab('tree');
      if (opts?.label) toast.success(opts.label);
      return true;
    }
    toast.error('The candidate spec is not valid and could not be loaded.');
    return false;
  }, []);

  const handleAuthored = useCallback(
    (result: AuthorCockpitResult) => {
      if (loadSpec(result.spec)) {
        // The author route ALREADY persisted this as a new version; mark the
        // loaded spec as the saved baseline so we don't re-save identical bytes.
        setSavedSpecJson(JSON.stringify(parseCockpitSpec(result.spec).spec ?? result.spec));
        setRevLabel(result.revLabel);
        toast.success(`Authored cockpit loaded (v${result.revLabel}) — review and publish.`);
      }
    },
    [loadSpec],
  );

  // ── Save / publish ─────────────────────────────────────────────────────
  const persist = useCallback(
    async (publish: boolean) => {
      if (readOnly) return;
      if (publish ? !affordances.canPublish : !affordances.canSaveDraft) return;
      // Hard gate: never persist an invalid spec.
      const check = parseCockpitSpec(spec);
      if (!check.ok || !check.spec) {
        toast.error(
          `Fix ${check.errors.length} validation ${check.errors.length === 1 ? 'issue' : 'issues'} before ${publish ? 'publishing' : 'saving'}.`,
        );
        return;
      }
      setIsSaving(true);
      try {
        const sandboxConfig = { cockpitSpec: check.spec };
        if (publish) {
          await publishMutation.mutateAsync({
            dashboardId: dashboard.id,
            revLabel,
            codeHtml: '',
            codeJs: '',
            codeCss: '',
            sandboxConfig,
          });
          toast.success(`Published v${revLabel}`);
        } else {
          await createVersion.mutateAsync({
            dashboardId: dashboard.id,
            revLabel,
            codeHtml: '',
            codeJs: '',
            codeCss: '',
            sandboxConfig,
          });
          toast.success('Draft saved');
        }
        setSavedSpecJson(JSON.stringify(check.spec));
      } catch (err) {
        toast.error(`${publish ? 'Publish' : 'Save'} failed: ${(err as Error).message}`);
      } finally {
        setIsSaving(false);
      }
    },
    [readOnly, affordances, spec, dashboard.id, revLabel, publishMutation, createVersion],
  );

  // ── Preview spec: only render a VALID spec (so a mid-edit invalid tree
  // doesn't blank the preview); fall back to the last-known-good rendered. ──
  const previewSpec = validation.ok && validation.spec ? validation.spec : spec;

  const issueCount = validation.ok ? 0 : validation.errors.length;
  const activeParentLabel = selectedNode
    ? getCockpitComponent(selectedNode.component)?.label ?? selectedNode.component
    : getCockpitComponent(activeRoot.component)?.label ?? 'Page';

  return (
    <div className="flex h-full flex-col">
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border/40 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <Sparkles className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-semibold">Visual cockpit editor</span>
          <Badge variant="outline" className="text-[9px]">spec</Badge>
          {validation.ok ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-3.5 w-3.5" /> Valid
            </span>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-help items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-3.5 w-3.5" /> {issueCount} {issueCount === 1 ? 'issue' : 'issues'}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-md">
                <ul className="list-disc pl-4 text-xs">
                  {validation.errors.slice(0, 8).map((e, i) => (
                    <li key={i}>
                      <span className="font-mono">{e.path}</span>: {e.message}
                    </li>
                  ))}
                  {validation.errors.length > 8 ? <li>…and {validation.errors.length - 8} more</li> : null}
                </ul>
              </TooltipContent>
            </Tooltip>
          )}
          {isDirty ? <span className="text-[10px] text-muted-foreground">• unsaved</span> : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">rev</span>
            <input
              aria-label="Revision label"
              type="text"
              className="h-6 w-16 rounded border border-input bg-background px-2 font-mono text-xs"
              value={revLabel}
              onChange={(e) => setRevLabel(e.target.value)}
              disabled={readOnly}
            />
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline" size="sm"
                disabled={!affordances.canSaveDraft || isSaving || !validation.ok}
                onClick={() => void persist(false)}
                data-testid="cockpit-save-draft"
              >
                {isSaving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                Save Draft
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {!validation.ok ? 'Fix validation issues first' : 'Save a draft version'}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                disabled={!affordances.canPublish || isSaving || publishMutation.isPending || !validation.ok}
                onClick={() => void persist(true)}
                data-testid="cockpit-publish"
              >
                {publishMutation.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Rocket className="mr-1.5 h-3.5 w-3.5" />}
                Publish
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {!validation.ok ? 'Fix validation issues first' : 'Publish to your tenant'}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* ── Body: palette | preview | inspector ─────────────────────────── */}
      <div className="min-h-0 flex-1">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Palette */}
          <ResizablePanel defaultSize="20%" minSize="14%" maxSize="32%" className="min-w-0">
            <CockpitPalette
              onInsert={handleInsert}
              disabled={readOnly}
              parentLabel={activeParentLabel}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Preview */}
          <ResizablePanel defaultSize="48%" minSize="28%">
            <div className="flex h-full flex-col">
              <div className="flex shrink-0 items-center justify-between border-b border-border/40 px-3 py-1.5">
                <span className="text-[11px] font-medium text-muted-foreground">Live preview</span>
                {!validation.ok ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                    <RefreshCw className="h-3 w-3" /> showing last valid
                  </span>
                ) : null}
              </div>
              <div className="min-h-0 flex-1 overflow-auto bg-muted/20" data-testid="cockpit-preview">
                <CockpitRenderer spec={previewSpec} theme={mode === 'dark' ? 'dark' : 'light'} />
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Inspector */}
          <ResizablePanel defaultSize="32%" minSize="22%" className="min-w-0">
            <div className="flex h-full flex-col">
              {/* Page tabs span the inspector top so multi-page is always visible. */}
              <CockpitPageTabs
                pages={pages}
                activeIndex={activePageIndex}
                canAddPage={canAddPageFn(spec)}
                disabled={readOnly}
                onSelect={handleSelectPage}
                onAdd={handleAddPage}
                onRename={handleRenamePage}
                onReorder={handleReorderPage}
                onDelete={handleDeletePage}
              />
              <Tabs
                value={inspectorTab}
                onValueChange={(v) => setInspectorTab(v as InspectorTab)}
                className="flex min-h-0 flex-1 flex-col"
              >
                <TabsList className="mx-2 mt-2 grid grid-cols-4">
                  <TabsTrigger value="tree" className="text-xs" data-testid="cockpit-tab-tree">
                    <ListTree className="mr-1 h-3.5 w-3.5" /> Tree
                  </TabsTrigger>
                  <TabsTrigger value="props" className="text-xs" data-testid="cockpit-tab-props">
                    <SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Props
                  </TabsTrigger>
                  <TabsTrigger value="pages" className="text-xs" data-testid="cockpit-tab-pages">
                    <Sparkles className="mr-1 h-3.5 w-3.5" /> AI
                  </TabsTrigger>
                  <TabsTrigger value="bindings" className="text-xs" data-testid="cockpit-tab-bindings">
                    <Database className="mr-1 h-3.5 w-3.5" /> Data
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="tree" className="mt-0 min-h-0 flex-1 overflow-y-auto px-2">
                  <CockpitTree
                    root={activeRoot}
                    selectedId={selectedNodeId}
                    onSelect={setSelectedNodeId}
                    onMove={handleMove}
                    onDelete={handleDelete}
                    disabled={readOnly}
                  />
                </TabsContent>

                <TabsContent value="props" className="mt-0 min-h-0 flex-1 overflow-y-auto p-3">
                  {selectedNode ? (
                    <div>
                      <div className="mb-2 flex items-center gap-2">
                        <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold">
                          {getCockpitComponent(selectedNode.component)?.label ?? selectedNode.component}
                        </span>
                        <Badge variant="outline" className="font-mono text-[9px]">{selectedNode.id}</Badge>
                      </div>
                      <Separator className="mb-2.5" />
                      <CockpitPropsForm
                        fields={selectedFields}
                        props={selectedNode.props ?? {}}
                        bindingIds={spec.declaredBindingIds}
                        actionIds={spec.declaredActionIds}
                        onChange={readOnly ? () => {} : handlePropsChange}
                        errorsByPath={selectedErrorsByPath}
                      />
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Select a component in the Tree tab to edit its properties.
                    </p>
                  )}
                </TabsContent>

                <TabsContent value="pages" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                  <CockpitAiAuthorPanel
                    dashboardId={dashboard.id}
                    bindingIds={spec.declaredBindingIds}
                    actionIds={spec.declaredActionIds}
                    disabled={readOnly}
                    onAuthored={handleAuthored}
                    onLoadCandidate={(c) =>
                      loadSpec(c, { label: 'Loaded best-effort draft — finish it by hand.' })
                    }
                  />
                </TabsContent>

                <TabsContent value="bindings" className="mt-0 min-h-0 flex-1 overflow-y-auto">
                  <DeclaredRefsPanel
                    bindingIds={spec.declaredBindingIds}
                    actionIds={spec.declaredActionIds}
                    disabled={readOnly}
                    onAddBinding={(id) => mutate(addBindingId(spec, id))}
                    onRemoveBinding={(id) => mutate(removeBindingId(spec, id))}
                    onAddAction={(id) => mutate(addActionId(spec, id))}
                    onRemoveAction={(id) => mutate(removeActionId(spec, id))}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Read-only footer notice */}
      {readOnly ? (
        <div className="shrink-0 border-t border-amber-500/30 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
          You have view-only access to this cockpit. Editing is disabled.
        </div>
      ) : null}
    </div>
  );
}
